// Gamification service (spec §11.5).
//
// Reads/writes users/{userId}/gamification/summary and per-day activity
// at users/{userId}/gamification/actions/{YYYY-MM-DD}. UI surfaces
// subscribe via subscribeGamificationSummary(); writes happen at the
// action site (e.g. handleNodeCompletion → recordGamificationAction).
//
// Streak logic is local-day based: a single day extends the streak by 1
// if lastActiveDate was yesterday, holds it if today, and resets to 1
// otherwise — except when the user is on Pro/Teams and has streak-freeze
// budget left, in which case a single missed day is auto-absorbed.

import {
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
  arrayUnion,
  increment,
  collection,
  query,
  orderBy,
  limit as fbLimit,
  getDocs,
} from 'firebase/firestore';
import { db } from './firebase';
import {
  POINTS_FOR,
  type GamificationAction,
  ACHIEVEMENTS_CATALOG,
  lookupAchievement,
} from '../constants/points-config';
import {
  EMPTY_GAMIFICATION,
  type AchievementRecord,
  type DailyActivityRecord,
  type UserGamification,
} from '../types/gamification';

// ─── Refs ───

function summaryRef(userId: string) {
  return doc(db, 'users', userId, 'gamification', 'summary');
}
function actionsCollection(userId: string) {
  return collection(db, 'users', userId, 'gamification', 'summary', 'daily');
}
function dailyRef(userId: string, dateKey: string) {
  return doc(db, 'users', userId, 'gamification', 'summary', 'daily', dateKey);
}

// ─── Date helpers ───

function todayLocalDate(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function thisMonthAnchor(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function daysBetween(a: string, b: string): number {
  const da = new Date(a + 'T00:00:00');
  const dbb = new Date(b + 'T00:00:00');
  return Math.round((dbb.getTime() - da.getTime()) / (24 * 60 * 60 * 1000));
}

// ─── Doc lifecycle ───

export async function ensureGamificationDoc(userId: string): Promise<UserGamification> {
  const ref = summaryRef(userId);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    // Backfill any new fields added after this doc was first created so
    // downstream readers can rely on the full shape. Cheap idempotent merge.
    const data = snap.data() as Partial<UserGamification>;
    const merged: UserGamification = {
      userId,
      ...EMPTY_GAMIFICATION,
      ...data,
      streakFreeze: { ...EMPTY_GAMIFICATION.streakFreeze, ...(data.streakFreeze ?? {}) },
      createdAt: (data.createdAt as string) ?? new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    return merged;
  }
  const now = new Date().toISOString();
  const fresh: UserGamification = {
    userId,
    ...EMPTY_GAMIFICATION,
    createdAt: now,
    updatedAt: now,
  };
  await setDoc(ref, {
    ...fresh,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return fresh;
}

export async function getGamificationSummary(userId: string): Promise<UserGamification> {
  return ensureGamificationDoc(userId);
}

export function subscribeGamificationSummary(
  userId: string,
  callback: (summary: UserGamification | null) => void,
): () => void {
  return onSnapshot(summaryRef(userId), (snap) => {
    callback(snap.exists() ? (snap.data() as UserGamification) : null);
  });
}

// ─── XP & per-day activity log ───

export async function awardXp(
  userId: string,
  action: GamificationAction,
  multiplier: number = 1,
): Promise<number> {
  const points = Math.round(POINTS_FOR[action] * multiplier);
  if (points <= 0) return 0;
  await ensureGamificationDoc(userId);
  await updateDoc(summaryRef(userId), {
    totalXp: increment(points),
    updatedAt: serverTimestamp(),
  });
  await logDailyActivity(userId, action, points);
  return points;
}

async function logDailyActivity(
  userId: string,
  action: GamificationAction,
  xpEarned: number,
): Promise<void> {
  const dateKey = todayLocalDate();
  const ref = dailyRef(userId, dateKey);
  const snap = await getDoc(ref);
  const now = new Date().toISOString();
  if (snap.exists()) {
    await updateDoc(ref, {
      xpEarned: increment(xpEarned),
      [`actionCounts.${action}`]: increment(1),
      updatedAt: now,
    });
  } else {
    const fresh: DailyActivityRecord = {
      date: dateKey,
      xpEarned,
      actionCounts: { [action]: 1 },
      updatedAt: now,
    };
    await setDoc(ref, fresh);
  }
}

export async function loadRecentDailyActivity(
  userId: string,
  daysBack: number = 90,
): Promise<DailyActivityRecord[]> {
  const q = query(
    actionsCollection(userId),
    orderBy('date', 'desc'),
    fbLimit(daysBack),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data() as DailyActivityRecord);
}

// ─── Counters (mutate the summary fields used by achievement criteria) ───

export interface CounterMutations {
  totalNodesCompleted?: number;
  pathsCompleted?: number;
  fullRecalibrationsCompleted?: number;
  consecutivePerfectCheckpoints?: number;
  /** Set absolute (used to reset the perfect-run streak on a non-perfect score). */
  consecutivePerfectCheckpointsAbsolute?: number;
  checkpointsPassedCount?: number;
  goDeeperModeCount?: number;
  testMeModeCount?: number;
  voiceTutorSessionsCount?: number;
  knowledgeGraphNodesExplored?: number;
  contradictionsFound?: number;
  /** Set true via mutateCounters; never unset. */
  diagnosticCompleted?: boolean;
  /** Subjects to add to the Polyglot set. Deduped server-side via arrayUnion. */
  addSubjects?: string[];
}

export async function mutateCounters(
  userId: string,
  mutations: CounterMutations,
): Promise<void> {
  await ensureGamificationDoc(userId);
  const update: Record<string, unknown> = { updatedAt: serverTimestamp() };
  for (const [key, value] of Object.entries(mutations)) {
    if (value === undefined) continue;
    if (key === 'consecutivePerfectCheckpointsAbsolute') {
      update.consecutivePerfectCheckpoints = value;
    } else if (key === 'addSubjects' && Array.isArray(value) && value.length > 0) {
      update.subjectsUploaded = arrayUnion(...value);
    } else if (key === 'diagnosticCompleted') {
      update.diagnosticCompleted = value;
    } else if (typeof value === 'number') {
      update[key] = increment(value);
    }
  }
  await updateDoc(summaryRef(userId), update);
}

// ─── Achievements ───

/** Idempotent for the same {userId, achievementId} pair. */
export async function recordAchievement(
  userId: string,
  achievementId: string,
): Promise<AchievementRecord | null> {
  const def = lookupAchievement(achievementId);
  if (!def) return null;
  const existing = await getGamificationSummary(userId);
  if (existing.achievements.some((a) => a.id === achievementId)) return null;

  const record: AchievementRecord = {
    id: def.id,
    earnedAt: new Date().toISOString(),
    xpAwarded: def.xpReward,
  };
  await updateDoc(summaryRef(userId), {
    achievements: arrayUnion(record),
    totalXp: increment(def.xpReward),
    updatedAt: serverTimestamp(),
  });
  return record;
}

/** Pure helper — given a current summary and an achievement id, returns
 *  whether the criterion appears satisfied. Encoded directly here so the
 *  frontend can preview "you're 1 node away" UX without a round-trip. */
export function hasMetAchievementCriterion(
  summary: UserGamification,
  achievementId: string,
): boolean {
  switch (achievementId) {
    case 'first_print':         return summary.diagnosticCompleted === true;
    case 'deep_diver':          return summary.goDeeperModeCount >= 10;
    case 'test_pilot':          return summary.checkpointsPassedCount >= 10;
    case 'polyglot':            return summary.subjectsUploaded.length >= 3;
    case 'week_warrior':        return summary.currentStreakDays >= 7;
    case 'month_master':        return summary.currentStreakDays >= 30;
    case 'perfect_run':         return summary.consecutivePerfectCheckpoints >= 5;
    case 'path_pioneer':        return summary.pathsCompleted >= 1;
    case 'self_aware':          return summary.fullRecalibrationsCompleted >= 1;
    case 'voice_learner':       return summary.voiceTutorSessionsCount >= 10;
    case 'knowledge_mapper':    return summary.knowledgeGraphNodesExplored >= 50;
    case 'contradiction_finder':return summary.contradictionsFound >= 1;
    default: return false;
  }
}

/** Walks the catalog, unlocks any whose criterion is now met, and returns
 *  the records for those that just unlocked. Caller can show a toast. */
export async function evaluateAndUnlockAchievements(
  userId: string,
): Promise<AchievementRecord[]> {
  const summary = await getGamificationSummary(userId);
  const earned = new Set(summary.achievements.map((a) => a.id));
  const unlocked: AchievementRecord[] = [];
  for (const def of ACHIEVEMENTS_CATALOG) {
    if (earned.has(def.id)) continue;
    if (def.comingSoon) continue;
    if (!hasMetAchievementCriterion(summary, def.id)) continue;
    const record = await recordAchievement(userId, def.id);
    if (record) unlocked.push(record);
  }
  return unlocked;
}

// ─── Streak ───

export interface StreakTickResult {
  newStreak: number;
  /** True if a freeze covered a one-day gap. */
  freezeUsed: boolean;
  /** True if the streak was broken — caller may show recovery banner. */
  broken: boolean;
  previousStreak: number;
}

/** Updates streak counters based on today's activity. Idempotent within
 *  the same local day. tierAllowsFreezes (default false) gates the
 *  one-day-gap freeze behaviour for Pro/Teams (spec §11.5.8). */
export async function tickStreak(
  userId: string,
  options: { tierAllowsFreezes?: boolean } = {},
): Promise<StreakTickResult> {
  const summary = await ensureGamificationDoc(userId);
  const today = todayLocalDate();
  const last = summary.lastActiveDate;
  const monthAnchor = thisMonthAnchor();

  // Roll the monthly freeze counter if the anchor changed.
  let usedThisMonth = summary.streakFreeze.usedThisMonth;
  let storedAnchor = summary.streakFreeze.monthAnchor;
  if (storedAnchor !== monthAnchor) {
    usedThisMonth = 0;
    storedAnchor = monthAnchor;
  }

  if (last === today) {
    return {
      newStreak: summary.currentStreakDays,
      freezeUsed: false,
      broken: false,
      previousStreak: summary.previousStreakDays,
    };
  }

  let newStreak = summary.currentStreakDays;
  let freezeUsed = false;
  let broken = false;
  let previousStreak = summary.previousStreakDays;

  if (last && daysBetween(last, today) === 1) {
    newStreak = (summary.currentStreakDays || 0) + 1;
  } else if (
    last
    && daysBetween(last, today) === 2
    && options.tierAllowsFreezes
    && usedThisMonth < 2
  ) {
    // Auto-consume a freeze for a single missed day.
    newStreak = (summary.currentStreakDays || 0) + 1;
    freezeUsed = true;
    usedThisMonth += 1;
  } else if (last && daysBetween(last, today) > 1) {
    previousStreak = summary.currentStreakDays;
    newStreak = 1;
    broken = previousStreak > 0;
  } else {
    newStreak = 1; // first activity ever
  }

  const newLongest = Math.max(summary.longestStreakDays, newStreak);

  const update: Record<string, unknown> = {
    currentStreakDays: newStreak,
    longestStreakDays: newLongest,
    lastActiveDate: today,
    previousStreakDays: previousStreak,
    'streakFreeze.monthAnchor': storedAnchor,
    'streakFreeze.usedThisMonth': usedThisMonth,
    updatedAt: serverTimestamp(),
  };
  if (freezeUsed) {
    update['streakFreeze.totalUsed'] = increment(1);
  }
  await updateDoc(summaryRef(userId), update);

  // Award the configured streak XP whenever a new day extends or starts a streak.
  if (newStreak !== summary.currentStreakDays) {
    await awardXp(userId, 'streak_day_added');
    if (newStreak === 7) await awardXp(userId, 'week_streak_bonus');
    if (newStreak === 30) await awardXp(userId, 'month_streak_bonus');
  }

  return { newStreak, freezeUsed, broken, previousStreak };
}

// ─── Composite helper used by every action site ───
//
// Single entry point for "user just did X" — awards XP, mutates counters,
// ticks streak, evaluates achievements. Returns any newly-unlocked
// achievements so the caller can show a toast.
export interface RecordActionOptions {
  multiplier?: number;
  counterMutations?: CounterMutations;
  tickStreak?: boolean;
  tierAllowsFreezes?: boolean;
}

export async function recordGamificationAction(
  userId: string,
  action: GamificationAction,
  options: RecordActionOptions = {},
): Promise<{ xpAwarded: number; newAchievements: AchievementRecord[]; streak: StreakTickResult | null }> {
  const xpAwarded = await awardXp(userId, action, options.multiplier ?? 1);
  if (options.counterMutations) {
    await mutateCounters(userId, options.counterMutations);
  }
  let streak: StreakTickResult | null = null;
  if (options.tickStreak) {
    streak = await tickStreak(userId, { tierAllowsFreezes: options.tierAllowsFreezes });
  }
  const newAchievements = await evaluateAndUnlockAchievements(userId);
  return { xpAwarded, newAchievements, streak };
}

export { ACHIEVEMENTS_CATALOG };
